/**
 * LiveScore handles subscribing/unsubscribing to LivescoreDiffusionAdapter
 * and updating badges (live, live at, live in, clock).
 *
 * @module app/components/LiveScore
 */
import _ from 'lodash';
import Date from './Date';
import EventViewAdapter from '../views/EventViewAdapter';
import jQuery from 'jquery';
import moment from 'moment-timezone';
import LivescoreDiffusionAdapter from '../components/push/LivescoreDiffusionAdapter';
import SportsDataRepository from '../components/SportsDataRepository';

const HOURSMINUTES = 'HH:mm';
const DAYMONTH = 'DD MMM';

// Selectors
const LIVE_AT = '.live-at';
const LIVE_IN = '.live-in, .live-in-min';
const EVENT_START_TIME = '.eventStartTime';
const SHOW_FUTURE_START_TIME = '.showFutureStartTime';

let now = moment.utc();
let countdownRefs = {};

/**
 * Given event id returns the jQuery node for that event.
 *
 * @param {String} eventId - Event id from the event object from PDS
 * @returns {Object} - jQuery node
 */
const getNode = (eventId) => jQuery('#' + eventId);

/**
 * Given a Moment.js date object returns a date string in the format HH:mm for events today
 * or DD MMM HH:mm for events not today.
 *
 * @param {Object} date - Moment.js date object
 * @returns {String} - Formatted date string
 */
const formatEventDate = (date) => {
    const dateFormat = date.isSame(now, 'day') ? HOURSMINUTES : `${DAYMONTH} ${HOURSMINUTES}`;
    return Date.format(date, dateFormat);
};

/**
 * Given an event object returns an event status as a boolean.
 *
 * @param {Object} event - Event object from PDS
 * @returns {Boolean}
 */
const isLive = (event) => (event.isInPlay || event.betInRun || event.offtime);

/**
 * Given an event id displays event and subscribes it to LivescoreDiffusionAdapter.
 *
 * @param {String} eventId - Event id from the event object from PDS
 * @param {Object} content - Page content in the form of a jQuery element
 */
const showEvent = (eventId, content) => {
    const event = SportsDataRepository.getModel().events[eventId];

    if (event) {
        getNode(event.id).removeClass('displayNone');
        api.subscribe(event, content);
    }
};

/**
 * Given an event id removes the display of an event and
 * unsubscribe it from LivescoreDiffusionAdapter.
 *
 * @param {String} eventId - Event id from the event object from PDS
 */
const hideEvent = (eventId) => {
    const event = SportsDataRepository.getModel().events[eventId];

    if (event) {
        getNode(event.id).addClass('displayNone');
        api.unsubscribe(eventId);
    }
};

/**
 * Given an event checks if the event is live before
 * removing it from the countdownRefs array and subscribing it to
 * LivescoreDiffusionAdapter. If it is not live it updates the "live at" or
 * "live in" badge and countdown / start time.
 *
 * @param {Object} event - Event object from PDS
 * @param {Object} content - Page content in the form of a jQuery element
 */
const handleInPlayEvent = (event, content) => {
    delete countdownRefs[event.id];

    LivescoreDiffusionAdapter.subscribe(
        event.sportEn,
        event.id,
        EventViewAdapter.livescore(event.id, content).update
    );
};

/**
 * Given an event and minutes, hides start date/time, displays
 * countdown and adds to the countdownRefs array.
 *
 * @param {Object} event - Event object from PDS
 * @param {Integer} minutes - Minutes until event
 */
const startEventCountDown = (event, minutes) => {
    const $eventNode = getNode(event.id);
    $eventNode.find(LIVE_IN).removeClass('displayNone');
    $eventNode.find(EVENT_START_TIME).addClass('displayNone');

    countdownRefs[event.id] = $eventNode.find(SHOW_FUTURE_START_TIME).html(minutes).removeClass('displayNone');
};

/**
 * Given an event and date, format the date update the future start time and show the live at badge.
 *
 * @param {Object} event - Event object from PDS
 * @param {Object} date - Moment.js date object
 */
const showStartTime = (event, date) => {
    const $eventNode = getNode(event.id);
    $eventNode.find(LIVE_AT).removeClass('displayNone');
    $eventNode.find(SHOW_FUTURE_START_TIME).html(formatEventDate(date)).removeClass('displayNone');
    $eventNode.find(EVENT_START_TIME).addClass('displayNone');
};

/**
 * Given an event, checks status: started / about to start (an hour or less) / starting in more than an
 * hour and delegates to methods accordingly.
 *
 * @param {Object} event - Event object from PDS
 * @param {Object} content - Page content in the form of a jQuery element
 */
const handleStartTime = (event, content) => {
    const date = moment(event.startDateTime || getNode(event.id).find(EVENT_START_TIME).attr('datetime'));
    const minutes = isLive(event) ? 0 : date.diff(now, 'minutes');

    /**
     *
     *
     * @returns
     */
    const hasStarted = () => {
        if (minutes <= 0) {
            api.subscribe(event, content);
        } else {
            toStart();
        }
    };

    /**
     *
     *
     */
    const toStart = () => {
        if (minutes > 0 && minutes < 60) {
            startEventCountDown(event, minutes);
        } else {
            showStartTime(event, date);
        }
    };

    hasStarted();
};

/**
 * Public methods.
 *
 * @type {Object}
 */
const api = {
    /**
     * Given a pushed message adds or removes the event from the page.
     *
     * @param {Object} message - Push message from PDS
     * @param {Object} content - Page content in the form of a jQuery element
     */
    handleLivescore: (message, content) => {
        if (message.value) {
            showEvent(message.id, content);
        } else {
            hideEvent(message.id);
        }
    },

    /**
     * Given a pushed message sets hasStarted flag and makes sure it is unsubscribed from
     * LivescoreDiffusionAdapter. Delegates event status to handleStartTime.
     *
     * @param {Object} message - Push message from PDS
     * @param {Object} content - Page content in the form of a jQuery element
     */
    startDateTimeUpdateCase: (message, content) => {
        const event = SportsDataRepository.getModel().events[message.id];

        api.unsubscribe(event.id);
        handleStartTime(event, content);
    },

    /**
     * Countdown timer for events starting within the hour.
     *
     * @param {Object} content - Page content in the form of a jQuery element
     */
    setCountDownTimer: (content) => {
        /**
         *
         *
         * @param {any} $elem
         * @returns
         */
        const decrementTime = ($elem) => {
            const eventId = $elem.attr('data-event-id');
            const newValue = parseInt($elem.html(), 10) - 1;

            if (newValue <= 0) {
                delete countdownRefs[eventId];
                return startEvent($elem);
            }

            $elem.html(newValue);

            return true;
        };

        /**
         *
         *
         * @param {any} $elem
         * @returns
         */
        const startEvent = ($elem) => {
            const eventId = $elem.attr('data-event-id');
            const event = SportsDataRepository.getModel().events[eventId];

            api.subscribe(event, content);

            return false;
        };

        _.each(countdownRefs, decrementTime);

        setTimeout(api.setCountDownTimer, 60000);
    },

    /**
     * Given an event checks exists and is live before delegating to handleInPlayEvent.
     *
     * @param {Object} event - Event object from PDS
     * @param {Object} content - Page content in the form of a jQuery element
     */
    subscribe: (event, content) => {
        if (!event) {
            return null;
        }

        if (isLive(event)) {
            handleInPlayEvent(event, content);
        }
    },

    /**
     * Unsubscribes given event from LivescoreDiffusionAdapter.
     *
     * @param {String} eventId - id of the event to unsubscribe from livescore service.
     */
    unsubscribe: (eventId) => {
        LivescoreDiffusionAdapter.unsubscribe(eventId);
    },
};

export default api;
